import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

interface SubstanceResult {
  pesticide_residue_id: string;
  pesticide_residue_name: string;
  regulation_count: number;
  substance_status: string | null;
  category_short: string | null;
}

interface ProductResult {
  product_id: string;
  product_name: string;
  product_code: string | null;
  row_count: number;
}

interface RegulationResult {
  regulation_number: string;
  application_date: string | null;
  entry_into_force_date: string | null;
  regulation_url: string | null;
  product_count: number;
  news_only?: boolean;
  published_date?: string | null;
  post_title?: string | null;
  celex_number?: string | null;
}

type SearchMode = 'substance' | 'product' | 'regulation';
type StatusFilter = 'in-force' | 'upcoming' | 'all';

export default function PesticideSearch() {
  const [mode, setMode] = useState<SearchMode>('substance');
  const [status, setStatus] = useState<StatusFilter>('in-force');
  const [bannedOnly, setBannedOnly] = useState(false);
  const [query, setQuery] = useState('');
  const [substanceResults, setSubstanceResults] = useState<SubstanceResult[]>([]);
  const [productResults, setProductResults] = useState<ProductResult[]>([]);
  const [regulationResults, setRegulationResults] = useState<RegulationResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset results when mode, status, or banned filter changes; re-search if there's a query
  useEffect(() => {
    setSubstanceResults([]);
    setProductResults([]);
    setRegulationResults([]);
    setIsOpen(false);
    if (query.length >= 2) {
      fireSearch(query, mode, status, bannedOnly);
    }
  }, [mode, status, bannedOnly]);

  function fireSearch(value: string, searchMode: SearchMode, searchStatus: StatusFilter, banned: boolean) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setSubstanceResults([]);
      setProductResults([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const endpoint = searchMode === 'substance'
          ? `/api/pesticides/search`
          : searchMode === 'product'
          ? `/api/pesticides/search-by-product`
          : `/api/pesticides/search-by-regulation`;
        const bannedQ = searchMode === 'substance' && banned ? '&banned=true' : '';
        const url = `${API_BASE_URL}${endpoint}?q=${encodeURIComponent(value)}&status=${searchStatus}${bannedQ}`;
        const res = await fetch(url);
        if (res.ok) {
          const json = await res.json();
          if (searchMode === 'substance') {
            setSubstanceResults(json.results || []);
          } else if (searchMode === 'product') {
            setProductResults(json.results || []);
          } else {
            setRegulationResults(json.results || []);
          }
          setIsOpen(true);
        }
      } catch {
        // silently ignore
      } finally {
        setLoading(false);
      }
    }, 300);
  }

  function handleChange(value: string) {
    setQuery(value);
    fireSearch(value, mode, status, bannedOnly);
  }

  function handleSelectSubstance(residueId: string) {
    setIsOpen(false);
    setQuery('');
    navigate(`/substance/${encodeURIComponent(residueId)}`);
  }

  function handleSelectProduct(productId: string) {
    setIsOpen(false);
    setQuery('');
    navigate(`/product/${encodeURIComponent(productId)}`);
  }

  function handleSelectRegulation(regulationNumber: string) {
    setIsOpen(false);
    setQuery('');
    navigate(`/regulation/${encodeURIComponent(regulationNumber)}`);
  }

  function handleSelectRegulationNewsOnly(r: RegulationResult) {
    setIsOpen(false);
    setQuery('');
    if (r.regulation_url) {
      window.open(r.regulation_url, '_blank', 'noopener,noreferrer');
    }
  }

  // Backend now returns one row per substance with a pre-computed regulation_count
  const uniqueSubstances = useMemo(() => substanceResults.map(r => ({
    id: r.pesticide_residue_id,
    name: r.pesticide_residue_name.trim(),
    regulationCount: r.regulation_count,
    status: r.substance_status ?? null,
    category: r.category_short ?? null,
  })), [substanceResults]);

  // Backend now returns one row per product with a pre-computed row_count
  const uniqueProducts = useMemo(() => productResults.map(r => ({
    product_id: r.product_id,
    product_name: r.product_name,
    product_code: r.product_code,
    regulationCount: r.row_count,
  })), [productResults]);

  const hasResults = mode === 'substance' ? uniqueSubstances.length > 0
    : mode === 'product' ? uniqueProducts.length > 0
    : regulationResults.length > 0;

  return (
    <div className="dash-card dash-card--search">
      <div className="dash-card__header">
        <h3>Pesticide Legislation</h3>
      </div>
      <div className="dash-card__body" ref={wrapperRef}>
        {/* Mode tabs */}
        <div className="pest-tabs">
          <button
            className={`pest-tabs__btn ${mode === 'substance' ? 'pest-tabs__btn--active' : ''}`}
            onClick={() => setMode('substance')}
          >
            Search by Substance
          </button>
          <button
            className={`pest-tabs__btn ${mode === 'product' ? 'pest-tabs__btn--active' : ''}`}
            onClick={() => setMode('product')}
          >
            Search by Product
          </button>
          <button
            className={`pest-tabs__btn ${mode === 'regulation' ? 'pest-tabs__btn--active' : ''}`}
            onClick={() => setMode('regulation')}
          >
            Search by Regulation
          </button>
        </div>

        {/* Status filter */}
        <div className="pest-status-bar">
          {(['in-force', 'upcoming', 'all'] as StatusFilter[]).map(s => (
            <button
              key={s}
              className={`pest-status-btn ${status === s ? 'pest-status-btn--active' : ''}`}
              onClick={() => setStatus(s)}
            >
              {s === 'in-force' ? 'In Force' : s === 'upcoming' ? 'Upcoming' : 'All'}
            </button>
          ))}
        </div>

        {/* Banned-only toggle (substance mode only) */}
        {mode === 'substance' && (
          <label className="pest-banned-toggle">
            <input
              type="checkbox"
              checked={bannedOnly}
              onChange={e => setBannedOnly(e.target.checked)}
            />
            <span>Banned substances only</span>
          </label>
        )}

        {/* Search input */}
        <div className="pest-search">
          <input
            type="text"
            className="pest-search__input"
            placeholder={mode === 'substance' ? 'Search substance name...' : mode === 'product' ? 'Search product name...' : 'Search regulation number...'}
            value={query}
            onChange={e => handleChange(e.target.value)}
            onFocus={() => hasResults && setIsOpen(true)}
          />
          {loading && <span className="pest-search__spinner" />}

          {/* Substance results dropdown */}
          {isOpen && mode === 'substance' && uniqueSubstances.length > 0 && (
            <ul className="pest-search__dropdown">
              {uniqueSubstances.map(s => {
                const statusClass = s.status === 'Approved'
                  ? 'pest-search__substatus pest-search__substatus--approved'
                  : s.status === 'Pending'
                  ? 'pest-search__substatus pest-search__substatus--pending'
                  : s.status === 'Not approved'
                  ? 'pest-search__substatus pest-search__substatus--banned'
                  : 'pest-search__substatus pest-search__substatus--unknown';
                return (
                  <li
                    key={s.id}
                    className="pest-search__option pest-search__option--substance"
                    onClick={() => handleSelectSubstance(s.id)}
                  >
                    <div className="pest-search__substance-top">
                      <span className="pest-search__name">{s.name}</span>
                      <span className="pest-search__reg">{s.regulationCount} regulation{s.regulationCount !== 1 ? 's' : ''}</span>
                    </div>
                    {(s.category || s.status) && (
                      <div className="pest-search__substance-bottom">
                        {s.category && (
                          <span className="pest-search__subcat">{s.category}</span>
                        )}
                        {s.status && (
                          <span className={statusClass}>{s.status}</span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Product results dropdown */}
          {isOpen && mode === 'product' && uniqueProducts.length > 0 && (
            <ul className="pest-search__dropdown">
              {uniqueProducts.map(p => (
                <li
                  key={p.product_id}
                  className="pest-search__option"
                  onClick={() => handleSelectProduct(p.product_id)}
                >
                  <span className="pest-search__name">{p.product_name}</span>
                  <span className="pest-search__meta">
                    {p.product_code && <span className="pest-search__reg">{p.product_code}</span>}
                    <span className="pest-search__reg">{p.regulationCount} entries</span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Regulation results dropdown */}
          {isOpen && mode === 'regulation' && regulationResults.length > 0 && (
            <ul className="pest-search__dropdown">
              {regulationResults.map(r => (
                <li
                  key={`${r.regulation_number}-${r.news_only ? 'news' : 'mrl'}`}
                  className={`pest-search__option ${r.news_only ? 'pest-search__option--news-only' : ''}`}
                  onClick={() => r.news_only ? handleSelectRegulationNewsOnly(r) : handleSelectRegulation(r.regulation_number)}
                  title={r.news_only ? 'Published but MRL data not yet available. Opens EUR-Lex in a new tab.' : undefined}
                >
                  <div className="pest-search__substance-top">
                    <span className="pest-search__name">{r.regulation_number}</span>
                    {r.news_only ? (
                      <span className="pest-search__reg-tag pest-search__reg-tag--news">News · EUR-Lex</span>
                    ) : (
                      <span className="pest-search__reg">{r.product_count} products</span>
                    )}
                  </div>
                  {r.news_only ? (
                    <div className="pest-search__substance-bottom">
                      {r.post_title && (
                        <span className="pest-search__news-title">{r.post_title}</span>
                      )}
                      {r.published_date && (
                        <span className="pest-search__reg">Published {r.published_date}</span>
                      )}
                      <span className="pest-search__news-note">MRL data pending upstream</span>
                    </div>
                  ) : (
                    <div className="pest-search__substance-bottom">
                      {r.application_date && (
                        <span className="pest-search__reg">Applies {r.application_date}</span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {isOpen && query.length >= 2 && !hasResults && !loading && (
            <div className="pest-search__dropdown pest-search__empty">
              No results found
            </div>
          )}
        </div>

        <div className="pest-search__quick">
          <span className="pest-search__quick-label">Quick links:</span>
          {(mode === 'substance'
            ? ['Glyphosate', 'Chlorpyrifos', 'Acetamiprid']
            : mode === 'product'
            ? ['Apples', 'Rice', 'Wheat']
            : ['2025', '2024', '2023']
          ).map(name => (
            <button
              key={name}
              className="pest-search__quick-btn"
              onClick={() => handleChange(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
